// Preset anchor: the position the buoy is moored at. Signed decimal degrees --
// 38° 22.204' N, 8° 58.100' W (22.204/60 = 0.370067, 58.100/60 = 0.968333; W is
// negative).
export const DEFAULT_ANCHOR = { lat: 38.370067, lon: -8.968333 };

const EARTH_RADIUS_M = 6371008.8; // IUGG mean radius

const toRad = (deg) => (deg * Math.PI) / 180;

/**
 * Great-circle distance in metres. Haversine rather than equirectangular: the
 * error matters at the scale a mooring watch cares about, and it costs
 * nothing here.
 */
export function haversineMeters(a, b) {
  if (!a || !b) return null;
  if (a.lat == null || a.lon == null || b.lat == null || b.lon == null) return null;

  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export function formatDistance(meters) {
  if (meters == null) return '--';
  if (meters < 1000) return `${meters.toFixed(0)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

/**
 * Accepts either a signed decimal ("-8.971944") or a magnitude with a
 * hemisphere suffix ("8.971944 W", "38.395278N"). Returns null rather than
 * NaN on anything it can't read, so callers can reject input cleanly.
 */
export function parseCoordinate(raw, axis) {
  if (typeof raw !== 'string') return null;

  const text = raw.trim().replace(/°/g, '').toUpperCase();
  if (!text) return null;

  const match = /^(-?\d+(?:\.\d+)?)\s*([NSEW])?$/.exec(text);
  if (!match) return null;

  const magnitude = Number(match[1]);
  const hemisphere = match[2];
  if (!Number.isFinite(magnitude)) return null;

  let value = magnitude;
  if (hemisphere) {
    const negative = hemisphere === 'S' || hemisphere === 'W';
    value = Math.abs(magnitude) * (negative ? -1 : 1);
  }

  const limit = axis === 'lat' ? 90 : 180;
  if (Math.abs(value) > limit) return null;

  return value;
}

export function formatCoordinate(value, axis) {
  if (value == null) return '--';

  const hemisphere = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
  return `${Math.abs(value).toFixed(6)}° ${hemisphere}`;
}
